/*
 * The formatted paragraph of a pop-up campaign. It is stored as plain text
 * with a small markup — never HTML, so nothing is injected as markup where it
 * is shown, and a paragraph without any (every campaign made before) reads as
 * it always did:
 *
 *   **bold**   _italic_   ~~struck~~   ==accent==        styles, may nest
 *   [text](https://…)                                    link (http(s) only)
 *   {code:SPRING20}                                      promo code, with Copy
 *   - item                                               a line in a list
 *   {center}line   {right}line                           a line's alignment (left: none)
 *   {video:youtube:dQw4w9WgXcQ}                          the first line only: a YouTube video
 *                                                        shown above the text, playing muted
 *
 * A backslash keeps a markup character as text (`\*`); the editor escapes
 * what is typed, so only the editor's own formatting becomes markup.
 *
 * espy-dashboard has a copy (function/campaign/paragraphFormat.ts) and draws
 * it (component/CampaignParagraph.tsx) — the two apps share no code, so a
 * change to the format goes into both.
 */

export const PARAGRAPH_MAX_LINKS = 3
export const PARAGRAPH_MAX_CODES = 1
export const PARAGRAPH_MAX_LIST_ITEMS = 5
export const PROMO_CODE = /^[A-Za-z0-9_-]{1,20}$/

export type Style = 'b' | 'i' | 's' | 'a'
export type Inline =
  | { t: 'text'; v: string }
  | { t: 'link'; v: string; href: string }
  | { t: 'code'; v: string }
  | { t: Style; c: Inline[] }
export type Align = 'center' | 'right'
export type Block = { t: 'line'; c: Inline[]; align?: Align } | { t: 'list'; items: Inline[][] }

/** outermost first — the order the serializer nests them in */
export const MARKERS: [Style, string][] = [['b', '**'], ['s', '~~'], ['a', '=='], ['i', '_']]

const LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s()]+)\)/y
const CODE = /\{code:([A-Za-z0-9_-]{1,20})\}/y
const ESCAPABLE = '\\*_[]{}~=-'

function atomAt(s: string, i: number): { node: Inline; end: number } | null {
  if (s[i] === '[') {
    LINK.lastIndex = i
    const m = LINK.exec(s)
    if (m) return { node: { t: 'link', v: m[1], href: m[2] }, end: i + m[0].length }
  }
  if (s[i] === '{') {
    CODE.lastIndex = i
    const m = CODE.exec(s)
    if (m) return { node: { t: 'code', v: m[1] }, end: i + m[0].length }
  }
  return null
}

/** where `marker` closes, stepping over escapes, links and codes */
function closeOf(s: string, marker: string, from: number): number {
  for (let i = from; i < s.length; i += 1) {
    if (s[i] === '\\') { i += 1; continue }
    const atom = atomAt(s, i)
    if (atom) { i = atom.end - 1; continue }
    if (s.startsWith(marker, i)) return i
  }
  return -1
}

export function parseInline(s: string): Inline[] {
  const out: Inline[] = []
  let buf = ''
  const flush = () => { if (buf) { out.push({ t: 'text', v: buf }); buf = '' } }
  let i = 0
  while (i < s.length) {
    if (s[i] === '\\' && i + 1 < s.length && ESCAPABLE.includes(s[i + 1])) { buf += s[i + 1]; i += 2; continue }
    const atom = atomAt(s, i)
    if (atom) { flush(); out.push(atom.node); i = atom.end; continue }
    const at = i
    const marker = MARKERS.find(([, m]) => s.startsWith(m, at))
    if (marker) {
      const [t, m] = marker
      const end = closeOf(s, m, i + m.length)
      if (end > i + m.length) {
        flush()
        out.push({ t, c: parseInline(s.slice(i + m.length, end)) })
        i = end + m.length
        continue
      }
    }
    buf += s[i]
    i += 1
  }
  flush()
  return out
}

/* ── the video ────────────────────────────────────────────────────── */

const VIDEO_LINE = /^\{video:youtube:([A-Za-z0-9_-]{11})\}(?:\n|$)/

/** the paragraph's video (a YouTube id) and its text without it */
export function splitVideo(value: string): { video: string | null; text: string } {
  const m = (value || '').match(VIDEO_LINE)
  return m ? { video: m[1], text: value.slice(m[0].length) } : { video: null, text: value || '' }
}

export const joinVideo = (video: string | null, text: string) => (video ? `{video:youtube:${video}}${text ? `\n${text}` : ''}` : text)

/** a YouTube link as pasted (watch, youtu.be, shorts, embed, live, or the bare id) → its id, or null */
export function youtubeId(input: string): string | null {
  const raw = input.trim()
  if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`)
    const host = url.hostname.replace(/^(www|m|music)\./, '')
    let id: string | null = null
    if (host === 'youtu.be') id = url.pathname.split('/')[1] || null
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      id = url.searchParams.get('v') || url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/)?.[1] || null
    }
    return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null
  } catch {
    return null
  }
}

/** the player: privacy-enhanced domain, starts by itself without sound, plays inline on phones */
export const youtubeEmbedUrl = (id: string) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1`
export const youtubeThumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`

/* ── reading it ───────────────────────────────────────────────────── */

export function parseParagraph(value: string): Block[] {
  const blocks: Block[] = []
  splitVideo(value).text.split('\n').forEach((line) => {
    if (line.startsWith('- ')) {
      const last = blocks[blocks.length - 1]
      const item = parseInline(line.slice(2))
      if (last?.t === 'list') last.items.push(item)
      else blocks.push({ t: 'list', items: [item] })
    } else {
      const m = line.match(/^\{(center|right)\}/)
      blocks.push(m ? { t: 'line', c: parseInline(line.slice(m[0].length)), align: m[1] as Align } : { t: 'line', c: parseInline(line) })
    }
  })
  return blocks
}

const inlineText = (nodes: Inline[]): string => nodes.map((n) => ('c' in n ? inlineText(n.c) : n.v)).join('')
const inlineAll = (blocks: Block[]) => blocks.flatMap((b) => (b.t === 'line' ? [b.c] : b.items))
const count = (nodes: Inline[], t: Inline['t']): number => nodes.reduce((n, x) => n + (x.t === t ? 1 : 0) + ('c' in x ? count(x.c, t) : 0), 0)

/** what the user reads — without markup and link addresses */
export const paragraphText = (value: string) =>
  parseParagraph(value).map((b) => (b.t === 'line' ? inlineText(b.c) : b.items.map(inlineText).join('\n'))).join('\n')

export function paragraphStats(value: string) {
  const blocks = parseParagraph(value)
  const lines = inlineAll(blocks)
  return {
    links: lines.reduce((n, l) => n + count(l, 'link'), 0),
    codes: lines.reduce((n, l) => n + count(l, 'code'), 0),
    listItems: Math.max(0, ...blocks.map((b) => (b.t === 'list' ? b.items.length : 0))),
  }
}

/* ── writing it ─────────────────────────────────────────────────────── */

export const escapeText = (v: string) => v.replace(/[\\*_[\]{}~=]/g, '\\$&')

/** `[text](href)`, with what would break the format taken out */
export const linkMarkup = (text: string, href: string) => `[${text.replace(/[[\]]/g, '').replace(/\s*\n\s*/g, ' ')}](${href})`

/** text with these styles, the spaces at its ends kept outside the markers */
export function styledMarkup(raw: string, styles: Set<Style>): string {
  if (!styles.size || !raw.trim()) return raw
  const [, lead, core, trail] = raw.match(/^(\s*)([\s\S]*?)(\s*)$/) as RegExpMatchArray
  const open = MARKERS.filter(([t]) => styles.has(t)).map(([, m]) => m)
  return lead + open.join('') + core + open.slice().reverse().join('') + trail
}

/** an address as typed → a safe http(s) one, or null. "espysys.com" becomes https://espysys.com/ */
export function normalizeUrl(input: string): string | null {
  const raw = input.trim()
  if (!raw) return null
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    if (!url.hostname.includes('.')) return null
    return url.href.replace(/\(/g, '%28').replace(/\)/g, '%29')
  } catch {
    return null
  }
}
